<template>
  <div class="w-full flex flex-col gap-0.5">
    <label
      v-if="props.label.length > 0"
      :for="props.id"
      class="w-full text-sm text-gray"
      :class="{
        '!text-red': state.isDirty && !state.isValid,
      }">
      {{ props.label }}
    </label>
    <input
      type="text"
      :id="props.id"
      class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
      :class="{
        '!border-red': state.isDirty && !state.isValid,
      }"
      v-model="state.textField"
      @input="onInput"
      @change="onChange" />
    <div
      v-if="state.isDirty && !state.isValid"
      class="text-sm text-red">
      <span
        v-for="error in state.errors"
        :key="error">
        {{ error }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, watch } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  id: { type: String, required: true },
  value: { type: String, required: true },
  isRequired: { type: Boolean, required: true },
  minLength: { type: Number, required: false },
  maxLength: { type: Number, required: false },
  triggerValidation: { type: Number, required: false },
});

const emit = defineEmits(['update:value', 'check-is-valid', 'on-change']);

const state = reactive({
  textField: '' as string,
  isDirty: false as boolean,
  isValid: false as boolean,
  errors: [] as Array<string>,
});

const validateField = () => {
  state.errors = [];
  if (props.isRequired && state.textField.length === 0) {
    state.errors.push('This field is required. Please fill it in. ');
  }

  if (props.minLength !== undefined && state.textField.length > 0 && state.textField.length < props.minLength) {
    state.errors.push(`This field must be at least ${props.minLength} characters long. `);
  }

  if (props.maxLength !== undefined && state.textField.length > props.maxLength) {
    state.errors.push(`The field cannot exceed ${props.maxLength} characters in length. `);
  }

  state.isValid = state.errors.length === 0;

  if (state.isDirty && !state.isValid) {
    emit('check-is-valid', false);
  } else {
    emit('check-is-valid', true);
  }
};

const onInput = () => {
  if (!state.isDirty) {
    state.isDirty = true;
  }

  validateField();

  emit('update:value', state.textField);
};

const onChange = () => {
  emit('on-change');
};

onMounted(() => {
  if (props.value !== undefined && props.value.length > 0) {
    state.textField = props.value;
  }
});

watch(
  () => props.value,
  () => {
    if (state.textField !== props.value) {
      state.textField = props.value;
      state.isDirty = true;
      validateField();
    }
  }
);

watch(
  () => props.triggerValidation,
  () => {
    state.isDirty = true;
    validateField();
  }
);
</script>
